import { ChangeDetectorRef, Component, ComponentRef, inject, OnInit, ViewChild, ViewContainerRef } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { FireflyMobileModalComponent, ModalContent } from '../../../../modal';
import type { FireflyChart, LineChartMobilePopoverContext } from '../../../common/models/common-chart-models';

@Component({
  selector: 'f-line-chart-mobile-popover',
  imports: [NgTemplateOutlet],
  template: `
    <div style="height: 300px">
      <ng-template #chartContainer></ng-template>
    </div>
    <hr />
    <div class="overflow-auto">
      <ng-container
        *ngTemplateOutlet="$any(context.template); context: { $implicit: context.popoverData }"
      ></ng-container>
    </div>
  `
})
export class FireflyLineChartMobilePopoverComponent implements OnInit, ModalContent {
  chart!: ComponentRef<FireflyChart>;
  context!: LineChartMobilePopoverContext;
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly modalContainer = inject(FireflyMobileModalComponent, { skipSelf: true });

  @ViewChild('chartContainer', { static: true, read: ViewContainerRef })
  private chartContainer!: ViewContainerRef;

  ngOnInit() {
    this.modalContainer.toggleAcceptBtnVisibility(false);

    if (!this.context.component) return;

    this.setUpChartInstance();
  }

  private setUpChartInstance() {
    this.chart = this.chartContainer.createComponent(this.context.component);

    this.applyBaseSettings();
    this.applyBoilerplateSettings();
    this.applyLineSettings();
    this.applyLegendSettings();
    this.setInitialPointerState();
    this.cdr.detectChanges();
  }

  private applyBaseSettings(): void {
    this.chart.instance.fitContainer = true;
    this.chart.instance.data = this.context.chartData;
    this.chart.instance.view = { width: window.innerWidth, height: 300 };
    this.chart.instance.interactions = { click: false, hover: false, preventMobile: true };
    this.chart.instance.condensedAxisOnMobile = false;
    this.chart.instance.animation = false;
  }

  private applyBoilerplateSettings(): void {
    this.chart.instance.showBorders = this.context.chartBoilerplate.showBorders;
    this.chart.instance.showYAxisDomain = this.context.chartBoilerplate.showYAxisDomain;
    this.chart.instance.showXAxisDomain = this.context.chartBoilerplate.showXAxisDomain;
    this.chart.instance.showXAxisTicksLabels = this.context.chartBoilerplate.showXAxisTicksLabels;
    this.chart.instance.showXAxisGridlines = this.context.chartBoilerplate.showXAxisGridlines;
    this.chart.instance.leftAxisLabelText = this.context.chartBoilerplate.leftAxisLabelText;
    this.chart.instance.xAxisTickFormatting = this.context.chartBoilerplate.xAxisTickFormatting;
    this.chart.instance.yAxisTickFormatting = this.context.chartBoilerplate.yAxisTickFormatting;
    this.chart.instance.integerYAxis = this.context.chartBoilerplate.integerYAxis;
  }

  private applyLineSettings(): void {
    this.chart.instance.lineClasses = this.context.lineClasses;
    this.chart.instance.lineWidth = this.context.lineWidth;
    this.chart.instance.circleRadius = this.context.circleRadius;
    this.chart.instance.showPointerLine = true;
  }

  private applyLegendSettings(): void {
    this.chart.instance.showLegend = Boolean(this.context.showLegend);
    if (!this.context.showLegend || !this.context.legendOptions) return;
    this.chart.instance.legendOptions = { ...this.context.legendOptions };
    this.chart.instance.legendOptions.legendContentAlignment = 'center';
    this.chart.instance.legendOptions.legendWrappingLinesCount = 10;
    this.chart.instance.legendOptions.legendPosition = 'bottom';
  }

  private setInitialPointerState(attempt = 0): void {
    // Defer until the next frame so layout/scales are computed before positioning the pointer.
    window.requestAnimationFrame(() => {
      const chartInstance = this.chart?.instance as FireflyChart & {
        activeDataPointId?: number;
        pointerXCoordinate?: number;
        dimensions?: { width: number; height: number };
      };
      const targetIndex = this.context.activeDataPointId ?? 0;
      const targetPoint = this.context.chartData?.[targetIndex];

      // Guard against missing chart instance or data while the modal mounts.
      if (!chartInstance || !targetPoint) return;
      const hasDimensions = !!chartInstance.dimensions?.width && !!chartInstance.dimensions?.height;
      // Wait until xScale/dimensions are ready; otherwise pointer math is wrong.
      if (!chartInstance.xScale || !hasDimensions) {
        if (attempt < 5) this.setInitialPointerState(attempt + 1);
        return;
      }

      // Compute pointer location using the same band math as the line path.
      const x = chartInstance.xScale(targetPoint.name) ?? 0;
      const bandWidth = chartInstance.xScale.bandwidth?.() ?? 0;
      const cx = x + bandWidth / 2;
      const firstSeriesValue = (targetPoint.series as { value?: unknown }[] | undefined)?.[0]?.value;
      const numericValue = Number(firstSeriesValue ?? 0);
      const yScale = chartInstance.yScale as unknown as ((value: number) => number) | undefined;
      const hasFiniteValue = Number.isFinite(numericValue);
      const cy = yScale && hasFiniteValue ? yScale(numericValue) : 0;
      const width = chartInstance.dimensions?.width ?? chartInstance.view?.width ?? 0;
      const height = chartInstance.dimensions?.height ?? chartInstance.view?.height ?? 0;

      // Clamp circle radius so the marker stays inside the chart bounds.
      if (width && height && Number.isFinite(cx) && Number.isFinite(cy)) {
        const maxRadius = Math.max(0, Math.min(cx, width - cx, cy, height - cy));
        const nextRadius = Math.min(this.context.circleRadius ?? 0, maxRadius);
        chartInstance.circleRadius = Math.max(0, nextRadius);
      }

      // Drive the line component’s pointer positioning with the selected datapoint.
      chartInstance.activeDataPointId = targetIndex;
      chartInstance.pointerXCoordinate = cx;
      this.chart.changeDetectorRef.detectChanges();

      // Re-run a few frames to handle late dimension updates.
      if (attempt < 5) this.setInitialPointerState(attempt + 1);
    });
  }
}
